/**
 * Витрина: категории, каталог, карточка товара, наличие по аптекам и похожие.
 *
 * Отбор делает сервер — он знает остатки и видимость; экран передаёт фильтры
 * параметрами запроса, а не режет загруженное. Цены и наличие приходят из FOM
 * и здесь только читаются.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { mediaUrl, type ApiClient, type components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'

type CategoryDto = components['schemas']['Category']
type ProductDto = components['schemas']['Product']
/**
 * `available` в контракте есть (openapi.yaml › Stock), а в сгенерированных
 * типах ещё нет — их перегенерируют вместе со следующей правкой контракта.
 */
type StockDto = components['schemas']['Stock'] & { available?: number }

export interface Category {
  id: number
  name: string
  /** Кэшбэк категории, % (R-17). */
  cb: number
  sort: number
  /** Сколько товаров видно в категории. */
  products: number
  /** Обложка из админки, полный адрес; пусто — обложки нет, встаёт значок. */
  cover: string
}

export interface ProductTexts {
  desc: string
  indications: string
  contra: string
  dosage: string
  storage: string
}

export interface Product {
  id: number
  name: string
  maker: string
  categoryId: number
  categoryName: string
  /** Итоговый процент кэшбэка: свой либо категорийный (R-12). */
  cb: number
  rx: boolean
  popular: boolean
  /** Готовые адреса картинок; пусто — фотографий нет. */
  photos: string[]
  /** Цена, сум; 0 — товар пока не продаётся. */
  price: number
  inStock: boolean
  /**
   * Сколько штук даст одна аптека — столько и возьмёт один заказ: аптека
   * собирает его целиком (R-26). Им ограничен «+», а не остатком по сети.
   */
  maxQty: number
  texts: ProductTexts
}

export interface Availability {
  storeId: number
  storeName: string
  district: string
  qty: number
  /**
   * Сколько аптека может отдать прямо сейчас: полка минус то, что держат живые
   * заказы (R-26). Старый сервер поля не присылает — тогда это полка.
   */
  available: number
  price: number
}

const EMPTY_TEXTS: ProductTexts = {
  desc: '',
  indications: '',
  contra: '',
  dosage: '',
  storage: '',
}

/**
 * Тексты витрины двуязычные. Перевода нет — показываем русский: пустая
 * карточка хуже карточки не на том языке (docs/frontend/api-and-mocks.md).
 */
export function toTexts(dto: components['schemas']['ProductTexts'] | undefined, lang: string) {
  const own = dto?.[lang]
  const ru = dto?.ru
  const pick = (field: keyof NonNullable<typeof own>): string => own?.[field] || ru?.[field] || ''
  if (!own && !ru) return EMPTY_TEXTS
  return {
    desc: pick('description'),
    indications: pick('indications'),
    contra: pick('contraindications'),
    dosage: pick('dosage'),
    storage: pick('storage'),
  }
}

export function toCategory(dto: CategoryDto): Category {
  return {
    id: dto.id ?? 0,
    // `name` — на языке запроса (X-Lang); перевода нет — сервер отдаёт русский.
    name: dto.name || dto.name_ru,
    cb: dto.cashback_pct ?? 0,
    sort: dto.sort ?? 0,
    products: dto.product_count ?? 0,
    cover: mediaUrl(dto.cover_url ?? '', env.apiBaseUrl),
  }
}

export function toProduct(dto: ProductDto, lang = 'ru'): Product {
  return {
    id: dto.id ?? 0,
    name: dto.name || dto.name_ru || '',
    maker: dto.maker ?? '',
    categoryId: dto.category_id ?? 0,
    categoryName: dto.category ?? '',
    cb: dto.cashback_pct ?? 0,
    rx: dto.is_rx ?? false,
    popular: dto.is_popular ?? false,
    photos: (dto.photos ?? []).map((path) => mediaUrl(path, env.apiBaseUrl)),
    price: dto.price ?? 0,
    inStock: dto.in_stock ?? false,
    maxQty: dto.max_qty ?? 0,
    texts: toTexts(dto.texts, lang),
  }
}

export type ProductSort = 'popular' | 'price_asc' | 'price_desc' | 'name'

export interface CatalogQuery {
  q?: string
  categoryId?: number
  rx?: 'rx' | 'otc' | ''
  inStock?: boolean
  priceMin?: number
  priceMax?: number
  sort?: ProductSort
}

export function toQuery(query: CatalogQuery) {
  return {
    q: query.q || undefined,
    category_id: query.categoryId || undefined,
    rx: query.rx || undefined,
    in_stock: query.inStock ?? undefined,
    price_min: query.priceMin || undefined,
    price_max: query.priceMax || undefined,
    sort: query.sort ?? 'popular',
    page_size: CATALOG_PAGE,
  }
}

/** Столько товаров приходит за раз; следующие догружаются по прокрутке. */
export const CATALOG_PAGE = 20

export interface CatalogPage {
  items: Product[]
  total: number
  /**
   * Сервер перестал считать на потолке (`total_capped`): совпадений больше,
   * чем `total`. Показываем «10 000+», а ленту листаем, пока страницы полные.
   */
  capped: boolean
}

export async function fetchProducts(
  api: ApiClient,
  query: CatalogQuery,
  lang: string,
  pageNumber = 1,
): Promise<CatalogPage> {
  const page = await api.get<{ items?: ProductDto[]; total?: number; total_capped?: boolean }>(
    '/catalog/products',
    { query: { ...toQuery(query), page: pageNumber } },
  )
  return {
    items: (page.items ?? []).map((p) => toProduct(p, lang)),
    total: page.total ?? 0,
    capped: page.total_capped ?? false,
  }
}

export function useCategories() {
  const api = useApi()
  return useQuery({
    queryKey: keys.categories,
    queryFn: async () => {
      const page = await api.get<{ items?: CategoryDto[] }>('/catalog/categories')
      return (page.items ?? []).map(toCategory).sort((a, b) => a.sort - b.sort)
    },
  })
}

/**
 * Каталог страницами: первая приходит сразу, следующие — когда покупатель
 * долистал до конца (`fetchNextPage`). Экран получает уже склеенный список
 * и общий итог сервера.
 */
export function useProducts(query: CatalogQuery, lang: string) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: keys.products({ ...toQuery(query), lang }),
    queryFn: ({ pageParam }) => fetchProducts(api, query, lang, pageParam),
    initialPageParam: 1,
    // Пустая страница — конец, даже если итог обещал больше: товар могли
    // снять с витрины, пока покупатель листал. Итог упёрся в потолок — ему не
    // верим: листаем, пока страницы приходят полными.
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
      const more = last.capped ? last.items.length === CATALOG_PAGE : loaded < last.total
      return last.items.length > 0 && more ? pages.length + 1 : undefined
    },
    select: (data): CatalogPage => ({
      items: data.pages.flatMap((page) => page.items),
      total: data.pages.at(-1)?.total ?? 0,
      capped: data.pages.at(-1)?.capped ?? false,
    }),
  })
}

/** Полка «Популярное». Пустой она не бывает: сервер добирает её лидерами (В-28). */
export function usePopular(lang: string) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.popular, lang],
    queryFn: async () => {
      const page = await api.get<{ items?: ProductDto[] }>('/catalog/popular')
      return (page.items ?? []).map((p) => toProduct(p, lang))
    },
  })
}

export function useProduct(id: number, lang: string) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.product(id), lang],
    queryFn: async () => toProduct(await api.get<ProductDto>(`/catalog/products/${id}`), lang),
    enabled: id > 0,
  })
}

export function toAvailability(dto: StockDto): Availability {
  const qty = dto.qty ?? 0
  return {
    storeId: dto.store_id ?? 0,
    storeName: dto.store ?? '',
    district: dto.district ?? '',
    qty,
    available: dto.available ?? qty,
    price: dto.price ?? 0,
  }
}

export async function fetchAvailability(api: ApiClient, id: number): Promise<Availability[]> {
  const page = await api.get<{ items?: StockDto[] }>(`/catalog/products/${id}/availability`)
  return (page.items ?? []).map(toAvailability)
}

/** В каких аптеках товар есть прямо сейчас (F-28). */
export function useAvailability(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.availability(id),
    queryFn: () => fetchAvailability(api, id),
    enabled: id > 0,
  })
}

export function useSimilar(id: number, lang: string) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.similar(id), lang],
    queryFn: async () => {
      const page = await api.get<{ items?: ProductDto[] }>(`/catalog/products/${id}/similar`)
      return (page.items ?? []).map((p) => toProduct(p, lang))
    },
    enabled: id > 0,
  })
}
